import { createStore } from 'vuex';
import axios from 'axios';
import rewards from '@/store/modules/rewards';
import checkout from '@/store/modules/checkout';
import forms from '@/store/modules/forms';
import pickupPoints from '@/store/modules/pickupPoints';
import addressSuggestions from '@/store/modules/addressSuggestions';
import { handleResponseError, getLineItemsGtmData } from '@/components/helper/util';

const widgetHelper = require('@/scripts/widgetHelper');

function hasSOREnabled(cart) {
    if (cart && cart.items) {
        return cart.items.some((item) => item.SorPeriodicity && item.SorPeriodicity !== undefined && item.SorPeriodicity !== null);
    }
    return false;
}

export default createStore({
    state: {
        actionURLs: window.vue.actionURLs,
        carousel: null,
        cart: {},
        contentSlots: window.vue.contentSlots,
        dyContentSlots: {
            miniCartPromotion: null
        },
        svgPath: window.vue.variables.svgPath,
        vueCurrentActiveStates: window.vueCurrentActiveStates,
        vueVariables: window.vue.variables || {}
    },
    getters: {
        cart(state) {
            return state.cart;
        },
        isCustomerAuthenticated(state) {
            return state.isCustomerAuthenticated;
        },
        carousel(state) {
            return state.carousel;
        },
        isSOREnabled(state) {
            return hasSOREnabled(state.cart);
        },
        dyContentSlots(state) {
            return state.dyContentSlots;
        },
        vueVariables(state) {
            return state.vueVariables;
        }
    },
    mutations: {
        setCart(state, data) {
            state.cart = data;
            if (state.vueCurrentActiveStates['mini-cart'] || state.vueCurrentActiveStates.cart) widgetHelper.refreshMiniCart({ quantityTotal: data.numItems });
            if (!data.keepSpinner) {
                widgetHelper.spinner.stop();
            }
            if (data && data.items && data.items.length > 0) {
                window.gtm_vars = Object.assign(window.gtm_vars || {}, getLineItemsGtmData(data.items, data.totals));
            }

            const quantity = data.numItems;
            window.EventEmitter?.emit('miniCartButton:updateQty', { quantity });
        },
        setCarousel(state, data) {
            state.carousel = data;
        },
        setContentSlots(state, data) {
            state.dyContentSlots[data.id] = data.value;
        },
        setVueVariables(state, data) {
            state.vueVariables = { ...state.vueVariables, ...data };
        }
    },
    actions: {
        getCarousel({ commit, state }) {
            if (window.vue.customPreferences.cartRecommendations) {
                const apiString = `${state.actionURLs['Page-IncludePageDesginer']}?cid=${window.vue.customPreferences.cartRecommendations}&type=slot`;
                return axios.get(apiString)
                    .then((response) => {
                        if (response.data.error) widgetHelper.createError(response.data.errorMessage || response.data.message);
                        else commit('setCarousel', response.data);
                    })
                    .catch((error) => {
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                    });
            }
        },

        getCart({ commit, dispatch, state }, payload) {
            const apiString = `${state.actionURLs['Cart-Get']}`;
            return axios.get(apiString)
                .then((response) => {
                    const data = response.data;

                    if (data.error) {
                        // eslint-disable-next-line no-throw-literal
                        throw { response };
                    }
                    commit('setCart', { ...data, keepSpinner: payload ? payload.keepSpinner : false });
                })
                .catch((error) => handleResponseError(commit, dispatch, error.response));
        },

        setCart({ commit }, payload) {
            return new Promise((resolve) => {
                commit('setCart', payload);
                resolve();
            });
        },

        getContentSlot({ commit, state }, payload) {
            const apiString = `${state.actionURLs['Cart-GetContentSlot']}`;
            return axios.get(apiString, { params: payload })
                .then((response) => {
                    const contentSlot = {
                        id: payload.id,
                        value: response.data
                    };
                    commit('setContentSlots', contentSlot);
                })
                .catch((error) => {
                    widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                });
        },

        removeProduct({ commit, state }, payload) {
            widgetHelper.spinner.start();

            let apiString = `${state.actionURLs['Cart-RemoveProductLineItem']}?pid=${payload.pid}&uuid=${payload.uuid}`;

            // eslint-disable-next-line no-unused-expressions
            payload.key ? apiString += `&key=${payload.key}` : null;

            return axios
                .get(apiString)
                .then((response) => {
                    if (response.data.error) {
                        if (response.data.redirectUrl) {
                            window.location.href = response.data.redirectUrl;
                        } else {
                            widgetHelper.createError(response.data.errorMessage || response.data.message);
                            widgetHelper.spinner.stop();
                        }
                    } else if (response.data.basket) {
                        if (window.EventEmitter) {
                            window.EventEmitter.emit('cart:removeProduct', {
                                ...response.data,
                                items: JSON.parse(JSON.stringify(window.gtm_vars.lineItems)),
                                pid: payload.pid,
                                rawPrice: payload.rawPrice
                            });
                        }
                        commit('setCart', response.data.basket);
                        if (window.vue.customPreferences.dyEnabled) {
                            dynamicYield.removeFromCart(payload.pid, payload.quantity);
                        }
                        widgetHelper.spinner.stop();
                    } else {
                        widgetHelper.createError(response.data.errorMessage || response.data.message);
                        widgetHelper.spinner.stop();
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                    }
                    widgetHelper.spinner.stop();
                });
        },

        addCouponCode({ commit, state }, payload) {
            widgetHelper.spinner.start();
            const apiString = `${state.actionURLs['Cart-AddCoupon']}?couponCode=${encodeURIComponent(payload.couponCode)}&csrf_token=${payload.csrfTokenValue}`;

            return new Promise((resolve, reject) => axios
                .get(apiString)
                .then(function (response) {
                    const data = response.data;
                    if (!data.error) {
                        var couponcode = [];
                        var couponLength = data.totals.discounts.length;
                        for (let i = 0; i < couponLength; i++) {
                            if (data.totals.discounts[i].type === 'coupon') {
                                couponcode.push(data.totals.discounts[i].couponCode);
                            }
                        }
                        if (window.vue.customPreferences.enableGtm) {
                            document.dispatchEvent(new CustomEvent('gtm:addcoupontocart', { detail: couponcode[couponcode.length - 1] }));
                        }
                        if (window.vue.customPreferences.dyEnabled) {
                            dynamicYield.applyPromoCode(payload.couponCode);
                        }
                        commit('setCart', data);
                        resolve(data);
                    } else {
                        widgetHelper.createError(data.errorMessage || data.message);
                        widgetHelper.spinner.stop();

                        if (window.vue.customPreferences.enableGtm) {
                            document.dispatchEvent(new CustomEvent('gtm:invalidpromocode', { detail: payload.couponCode }));
                        }
                        resolve(data);
                    }
                })
                .catch(function (error) {
                    if (error.response.status === 410) {
                        window.location.reload();
                    } else {
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                        widgetHelper.spinner.stop();
                    }
                    reject(error.response.data);
                }));
        },

        removeCouponCode({ commit, state }, payload) {
            widgetHelper.spinner.start();
            const apiString = `${state.actionURLs['Cart-RemoveCouponLineItem']}?code=${encodeURIComponent(payload.code)}&uuid=${payload.uuid}`;

            return new Promise((resolve, reject) => axios
                .get(apiString)
                .then(function (response) {
                    if (response.data.error) {
                        widgetHelper.createError(response.data.errorMessage || response.data.message);
                        widgetHelper.spinner.stop();
                    } else if (response.data) {
                        commit('setCart', response.data);
                        resolve(response.data);
                    }
                })
                .catch(function (error) {
                    if (error.response.status === 410) {
                        window.location.reload();
                    } else {
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                        widgetHelper.spinner.stop();
                    }
                    reject(error.response.data);
                }));
        },

        updateLineItemQuantity({ commit, state }, payload) {
            widgetHelper.spinner.start();
            const apiString = `${state.actionURLs['Cart-UpdateQuantity']}`;

            return new Promise((resolve) => axios({
                method: 'post',
                url: apiString,
                data: payload.editProductLineItemForm,
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(function (response) {
                    if (response.data.error && response.data.redirectUrl) {
                        window.location.href = response.data.redirectUrl;
                    } else if (response.data.errorMessage && !response.data.gratisError) {
                        widgetHelper.createError(response.data.errorMessage);
                        widgetHelper.spinner.stop();
                        resolve(response.data);
                    } else if (response.data.cartModel && response.data.cartModel.gratisErrorMessage && (response.data.cartModel.isGratisError || response.data.cartModel.isGratisMix)) {
                        if (!window.vueCurrentActiveStates.cart) {
                            widgetHelper.createError(response.data.cartModel.gratisErrorMessage);
                        }
                        commit('setCart', response.data.cartModel);
                        resolve(response.data);
                    } else if (response.data.cartModel) {
                        commit('setCart', response.data.cartModel);
                        if (response.data.eventType === 'increase') {
                            window.EventEmitter?.emit('cart:addProduct', {
                                ...response.data
                            });
                        } else if (window.EventEmitter) {
                            window.EventEmitter.emit('cart:removeProduct', {
                                ...response.data
                            });
                        }
                        if (window.vue.customPreferences.dyEnabled) {
                            if (response.data.dyData.eventType === 'increase') {
                                dynamicYield.addToCart(payload.editProductLineItemForm.get('pid'), response.data.dyData.quantity);
                            } else {
                                dynamicYield.removeFromCart(payload.editProductLineItemForm.get('pid'), response.data.dyData.quantity);
                            }
                        }
                        resolve(response.data);
                    } else {
                        widgetHelper.createError(response.data.errorMessage || response.data.message);
                        widgetHelper.spinner.stop();
                        resolve(response.data);
                    }
                })
                .catch(function (error) {
                    widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                    widgetHelper.spinner.stop();
                    resolve(error.response.data);
                }));
        },

        addProduct({ commit, state }, payload) {
            widgetHelper.spinner.start();
            let apiString;
            switch (payload.get('productType')) {
                case 'bonusProduct':
                    apiString = `${state.actionURLs['Cart-AddBonusProducts']}`;
                    break;
                default:
                    apiString = `${state.actionURLs['Cart-AddProduct']}`;
            }

            return axios({
                method: 'post',
                url: apiString,
                data: payload,
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then((response) => {
                    if (response.data.error) {
                        widgetHelper.createError(response.data.errorMessage || response.data.message);
                        widgetHelper.spinner.stop();
                        if (response.data.redirectUrl) {
                            window.location.href = response.data.redirectUrl;
                        }
                    } else if (response.data.cart) {
                        commit('setCart', response.data.cart);
                        response.data.pageContext = 'Cart Page';
                        document.dispatchEvent(new CustomEvent('gtm:addtocart', { detail: response.data }));
                        if (window.vue.customPreferences.dyEnabled) {
                            dynamicYield.addToCart(payload.get('pid'), payload.get('quantity'));
                        }
                    } else {
                        widgetHelper.createError(response.data.errorMessage || response.data.message);
                        widgetHelper.spinner.stop();
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                    }
                    widgetHelper.spinner.stop();
                });
        },

        sorUpdateProduct({ state, dispatch }, payload) {
            widgetHelper.spinner.start();
            const apiString = `${state.actionURLs['SmartOrderRefillController-UpdateCartProductRefillInformation']}`;

            return axios({
                method: 'post',
                url: apiString,
                data: payload,
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(() => {
                    dispatch('getCart');
                    dispatch('getRewards');
                })
                .catch((error) => {
                    if (error.response.status === 410) {
                        window.location.reload();
                    } else {
                        SmartOrderRefill.initErrorModal(
                            SmartOrderRefillSettings.Resources.SOR_UNEXPECTED_ERROR,
                            false
                        );
                        widgetHelper.createError(error.response.data.errorMessage || error.response.data.message);
                        widgetHelper.spinner.stop();
                    }
                });
        },

        getReportingUrls({ state }, payload) {
            const apiString = state.actionURLs[`ReportingEvent-${payload}`];
            return axios.get(apiString)
                .then(() => {
                })
                .catch(() => {
                });
        },

        getCheckoutHF({ state }) {
            axios.get(state.actionURLs['Checkout-GetHeader'])
                .then((response) => {
                    var template = document.createElement('template');
                    template.innerHTML = response.data.trim();

                    var checkoutHeader = template.content.querySelector('header');
                    checkoutHeader.classList.add('d-none');
                    document.getElementsByTagName('header')[0].after(checkoutHeader);
                })
                .catch(() => {});

            axios.get(state.actionURLs['Checkout-GetFooter'])
                .then((response) => {
                    var template = document.createElement('template');
                    template.innerHTML = response.data.trim();

                    var checkoutFooter = template.content.querySelector('footer');
                    checkoutFooter.classList.add('d-none');
                    document.getElementsByTagName('footer')[0].after(checkoutFooter);
                })
                .catch(() => {});
        },

        updateAbandonedCart({ state }, payload) {
            if (window.vue.customPreferences.abandonedCartEnabled && payload) {
                axios({
                    method: 'post',
                    url: state.actionURLs['AbandonedCart-Update'],
                    data: { status: payload },
                    headers: { 'Content-Type': 'application/json; charset=utf-8' }
                });
            }
        },

        async onSubmitPaypal({ state }, payload) {
            try {
                const response = await axios({
                    method: 'post',
                    url: state.actionURLs['AdyenPaypal-OnSubmit'],
                    data: payload,
                    headers: { 'Content-Type': 'application/json; charset=utf-8' }
                });
                return response.data;
            } catch { }
        },

        async cancelPaypal({ state }) {
            try {
                await axios({
                    method: 'post',
                    url: state.actionURLs['AdyenPaypal-Cancel'],
                    headers: { 'Content-Type': 'application/json; charset=utf-8' }
                });
            } catch { }
        },

        async onShopperDetails({ state }, payload) {
            try {
                const response = await axios({
                    method: 'post',
                    url: state.actionURLs['AdyenPaypal-OnShopperDetails'],
                    data: payload,
                    headers: { 'Content-Type': 'application/json; charset=utf-8' }
                });
                return response.data;
            } catch { }
        },

        async updatePayPalOrder({ state }, payload) {
            try {
                const response = await axios({
                    method: 'post',
                    url: state.actionURLs['AdyenPaypal-OnUpdateOrder'],
                    data: payload,
                    headers: { 'Content-Type': 'application/json; charset=utf-8' }
                });
                return response.data;
            } catch { }
        }

    },
    ...(window.vueCurrentActiveStates.cart ? {
        modules: {
            rewards,
            checkout,
            pickupPoints,
            addressSuggestions,
            forms
        }
    } : {})
});
